import {
  CircleXIcon,
  HStack,
  IconButton,
  Td,
  Tooltip,
  Tr,
  Flex,
  Text,
  CloseIcon,
} from '@frontend/components';

import CostCategoryBadge from 'components/lib2/CostCategoryBadge';
import { CustomCategoryBadge } from 'components/lib2/CustomCategoryBadge';

import { COST_CATEGORY } from 'constants/all';

import FormattedInput from 'modules/quotes/quoteItems/components/InputComponents/InputCells/FormattedInputCell';
import Cell from 'modules/quotes/quoteItems/components/Table/Cell';

import { formatPercent, parseDecimalString } from 'utils/formatters';

import type { FormProfitItem } from './types';

export type RemoveProfitItemHandler = (id: FormProfitItem['id']) => void;
export type EditProfitItemHandler = (
  id: FormProfitItem['id'],
  data: Partial<FormProfitItem>,
) => void;

export interface ProfitItemRowProps {
  profitItem: FormProfitItem;
  onRemove?: RemoveProfitItemHandler;
  onEdit: EditProfitItemHandler;
  error?: string;
}

const decimalParser = (s: string) => {
  const parsedString = parseDecimalString(s) ?? '';
  if (parsedString.at(0) === '-') {
    return parsedString.slice(1);
  } else {
    return parsedString;
  }
};

export const ProfitItemRow = ({
  profitItem,
  onEdit,
  onRemove,
  error,
}: ProfitItemRowProps) => {
  return (
    <Tr>
      <Td>
        <Flex alignItems="center">
          <HStack flex={1} justifyContent="space-between">
            <Text as="span" flex={1}>
              {profitItem.name}
            </Text>
            {profitItem.category === COST_CATEGORY.CUSTOM ? (
              <CustomCategoryBadge
                name={profitItem.customCategoryName}
                color={profitItem.customCategoryColor}
              />
            ) : (
              <CostCategoryBadge category={profitItem.category} />
            )}
          </HStack>
          {onRemove && (
            <IconButton
              ml={1}
              aria-label="Remove Pricing Item"
              onClick={() => onRemove(profitItem.id)}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Flex>
      </Td>
      <Cell
        as="td"
        pl={0.25}
        display="table-cell"
        revertable
        shouldHighlightOverrides
        border="none"
        w="unset"
        h={3}
        editable
        editableComponent={FormattedInput}
        formatter={(v: string) =>
          v === ''
            ? onRemove
              ? '(Calculated values)'
              : '(Leave unchanged)'
            : formatPercent(v)
        }
        parser={decimalParser}
        calculatedKey="percentage"
        manualKey="manualPercentage"
        source={profitItem}
        onSave={(manualPercentage: string | null) =>
          onEdit(profitItem.id, { manualPercentage })
        }
        {...(error
          ? {
              bg: 'alert.surfaceError',
              leftElement: (
                <Tooltip label={error}>
                  <CircleXIcon
                    aria-label="Invalid Pricing Item Warning"
                    tabIndex={0}
                    boxSize={3}
                    color="alert.error"
                    ml={1}
                  />
                </Tooltip>
              ),
            }
          : null)}
      />
    </Tr>
  );
};
